import { BlockStack, Card, Divider } from '@shopify/polaris';
import React from 'react';

import ColorPickerCustom from '@/components/ColorPickerCustom';
import { RedirectUrl } from '@/constants/enum';
import { handleChangePreview, previewSelector } from '@/redux/slice/preview.slice';
import { TextField } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import BoldText from '@/components/BoldText';
import { useTranslation } from 'react-i18next';
import EditorCustom from '@/components/EditorCustom';
import { cancelButtonInitial, submitButtonInitial } from '@/constants/options';
import { validatePositiveIntegerInput } from '@/helpers/string';
import { FontSize } from '@/components/EditorCustom/FontSizeButton';
import userPlans from '@/hooks/userPlans';
import CustomBadge from '@/components/CustomBadge';

interface ButtonSettingsProps {}

const ButtonSettings: React.FC<ButtonSettingsProps> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['popup']);
  const previewDetail = useSelector(previewSelector);
  const { isUserFree, isUserBasic, isUserFreeOrBasic } = userPlans();

  const handleChangeTemplate = (key: keyof typeof previewDetail) => (value: string) => {
    if (
      key === 'submitButtonBorderRadius' ||
      key === 'cancelButtonBorderRadius' ||
      key === 'submitButtonBorderWidth' ||
      key === 'cancelButtonBorderWidth'
    ) {
      value = validatePositiveIntegerInput(value);
    }

    dispatch(
      handleChangePreview({
        [key]: value,
      }),
    );
  };

  return (
    <BlockStack gap="200">
      <Card>
        <BlockStack gap="200">
          <BoldText>{t('submit_button')}</BoldText>
          <EditorCustom
            value={previewDetail?.submitButtonLabelEditor ?? previewDetail?.submitButtonLabel ?? submitButtonInitial(t)}
            onChange={handleChangeTemplate('submitButtonLabelEditor')}
            defaultFontSize={FontSize.Base}
            disabledFontFamily={isUserFreeOrBasic}
            disabledColor={isUserFree}
            disabledFontSize={isUserFree}
          />
        </BlockStack>
      </Card>

      <Card>
        <BoldText>
          {t('button_style')} {isUserFreeOrBasic && <CustomBadge />}
        </BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="500">
          <ColorPickerCustom
            onChange={handleChangeTemplate('submitButtonBackground')}
            label={t('background')}
            color={previewDetail.submitButtonBackground}
            disabled={isUserFree}
          />

          <ColorPickerCustom
            onChange={handleChangeTemplate('submitButtonBorderColor')}
            label={t('border_color')}
            color={previewDetail.submitButtonBorderColor}
            disabled={isUserFreeOrBasic}
          />

          <TextField
            autoComplete=""
            label={t('border_radius')}
            value={previewDetail.submitButtonBorderRadius}
            onChange={handleChangeTemplate('submitButtonBorderRadius')}
            suffix="px"
            disabled={isUserFreeOrBasic}
          />

          <TextField
            autoComplete=""
            label={t('border_width')}
            value={previewDetail.submitButtonBorderWidth}
            onChange={handleChangeTemplate('submitButtonBorderWidth')}
            suffix="px"
            disabled={isUserFreeOrBasic}
          />
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="200">
          <BoldText>{t('cancel_button')}</BoldText>
          <EditorCustom
            value={previewDetail?.cancelButtonLabelEditor ?? previewDetail?.cancelButtonLabel ?? cancelButtonInitial(t)}
            onChange={handleChangeTemplate('cancelButtonLabelEditor')}
            defaultFontSize={FontSize.Base}
            disabledFontFamily={isUserFree || isUserBasic}
            disabledColor={isUserFree}
            disabledFontSize={isUserFree}
          />
        </BlockStack>
      </Card>

      <Card>
        <BoldText>
          {t('button_style')} {isUserFreeOrBasic && <CustomBadge />}
        </BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="500">
          <ColorPickerCustom
            onChange={handleChangeTemplate('cancelButtonBackground')}
            label={t('background')}
            color={previewDetail.cancelButtonBackground}
            disabled={isUserFree}
          />

          <ColorPickerCustom
            onChange={handleChangeTemplate('cancelButtonBorderColor')}
            label={t('border_color')}
            color={previewDetail.cancelButtonBorderColor}
            disabled={isUserFreeOrBasic}
          />

          <TextField
            autoComplete=""
            label={t('border_radius')}
            value={previewDetail.cancelButtonBorderRadius}
            onChange={handleChangeTemplate('cancelButtonBorderRadius')}
            suffix="px"
            disabled={isUserFreeOrBasic}
          />

          <TextField
            autoComplete=""
            label={t('border_width')}
            value={previewDetail.cancelButtonBorderWidth}
            onChange={handleChangeTemplate('cancelButtonBorderWidth')}
            suffix="px"
            disabled={isUserFreeOrBasic}
          />
        </BlockStack>
      </Card>

      <Card>
        <BoldText>{t('visitor_setting')}</BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="500">
          <TextField
            autoComplete=""
            label={t('redirect_url')}
            value={previewDetail.redirectUrl}
            onChange={handleChangeTemplate('redirectUrl')}
            onBlur={() => {
              if (!previewDetail.redirectUrl.trim().length) handleChangeTemplate('redirectUrl')(RedirectUrl.Google);
            }}
          />

          <TextField
            autoComplete=""
            label={t('error_message')}
            value={previewDetail?.errorMessage || ''}
            onChange={handleChangeTemplate('errorMessage')}
            placeholder={t('enter_error_message')}
            maxLength={255}
            showCharacterCount
          />
        </BlockStack>
      </Card>
    </BlockStack>
  );
};

export default ButtonSettings;
